import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { useLanguage } from "../ui/LanguageProvider";
import { documentFamilyLabel, documentLabel } from "../../pages/automation/common/documentRegistry";
import {
  mergeDocumentCatalog,
  mergeDocumentFamilies,
  mergeModuleCatalog,
  mergeLinkCatalog,
  type CatalogLinkItem,
  type CatalogModuleItem,
  type DocumentCatalogItem,
  type DocumentFamilyItem,
  type DocumentCatalogOverride,
} from "../../data/documentCatalog";

/**
 * 문서 카탈로그 컨텍스트 — 코드 레지스트리 + DB 오버라이드(crm_document_catalog).
 *
 * MenuRoleProvider 와 같은 모양이다: 로그인하면 한 번 읽고, 실패하면 **코드 기본값으로 산다**.
 * 문서 목록이 네트워크 때문에 비는 일은 없어야 한다.
 *
 * ★ 이 컨텍스트는 등급을 모른다. 문서 접근 판정은
 *   `isDocumentEnabled(items, docKey)` (여기) AND `등급 판정` (MenuRoleContext) 둘을 곱한다.
 *   메뉴의 **사용/미사용**은 더 이상 문서를 막지 않는다 — 그게 이 파일이 생긴 이유다.
 */
type DocumentCatalogContextValue = {
  /** 코드 순서·이름에 오버라이드를 얹은 전체 목록 (사용/미사용 모두 포함) */
  items: DocumentCatalogItem[];
  /** 묶음(상위) — 이름·순서 오버라이드를 얹은 결과, 순서대로 */
  families: DocumentFamilyItem[];
  /** 문서가 아닌 실행 모듈(고객 자료 분석 · 3D) — 문서 관리 표·Opportunity 실행 로직용. 작성 레일엔 없다. */
  modules: CatalogModuleItem[];
  /** 문서 관리에서 추가한 바로가기("link-*" 행) — 이름 + 경로. 실행 로직 목록에서 누르면 그 화면을 연다. */
  links: CatalogLinkItem[];
  loaded: boolean;
  /** 마이그레이션 전이거나 조회 실패 — 코드 기본값으로 살고 있다는 뜻. 관리 화면이 알린다. */
  degraded: boolean;
  /** 이 문서가 사용 중인가 (등급은 보지 않는다) */
  isEnabled: (docKey: string) => boolean;
  refresh: () => Promise<void>;
};

const DocumentCatalogContext = createContext<DocumentCatalogContextValue | null>(null);

export function DocumentCatalogProvider({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const [overrides, setOverrides] = useState<DocumentCatalogOverride[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [degraded, setDegraded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/auth-users/document-catalog", { credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setOverrides(Array.isArray(data.documents) ? data.documents : []);
      setDegraded(Boolean(data.degraded));
    } catch {
      // 코드 기본값으로 산다 — 문서 목록이 비는 것보다 낫다.
      setOverrides([]);
      setDegraded(true);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (status === "authenticated") {
      void refresh();
    } else if (status === "unauthenticated") {
      setOverrides([]);
      setDegraded(false);
      setLoaded(true);
    }
  }, [status, refresh]);

  const value = useMemo<DocumentCatalogContextValue>(() => {
    const items = mergeDocumentCatalog(overrides);
    const enabledByKey = new Map(items.map((item) => [item.key, item.enabled]));
    return {
      items,
      families: mergeDocumentFamilies(overrides),
      modules: mergeModuleCatalog(overrides),
      links: mergeLinkCatalog(overrides),
      loaded,
      degraded,
      // 카탈로그가 모르는 키는 막지 않는다 — 레지스트리에 아직 없는 화면이
      // 원인 모를 "접근 권한 없음"이 되면 안 된다(지금 고치는 바로 그 증상).
      isEnabled: (docKey: string) => enabledByKey.get(docKey) ?? true,
      refresh,
    };
  }, [overrides, loaded, degraded, refresh]);

  return <DocumentCatalogContext.Provider value={value}>{children}</DocumentCatalogContext.Provider>;
}

/**
 * Provider 밖에서 부르면 **코드 기본값**을 돌려준다(예외를 던지지 않는다).
 * 문서 소비처는 앱 곳곳에 있고, 그중 하나가 Provider 밖이라는 이유로
 * 화면이 통째로 죽는 것보다 기본값으로 도는 편이 낫다.
 */
export function useDocumentCatalog(options?: { raw?: boolean }): DocumentCatalogContextValue {
  const ctx = useContext(DocumentCatalogContext);
  const { language } = useLanguage();
  const raw = options?.raw === true;
  const fallback = useMemo<DocumentCatalogContextValue>(
    () => ({
      items: mergeDocumentCatalog([]),
      families: mergeDocumentFamilies([]),
      modules: mergeModuleCatalog([]),
      links: [],
      loaded: true,
      degraded: true,
      isEnabled: () => true,
      refresh: async () => {},
    }),
    []
  );
  const base = ctx ?? fallback;
  // 영문 화면: 문서 관리에서 이름을 바꾸지 않은(label === codeLabel) 항목만 영문 표시명으로 바꾼다.
  //   바꾼 이름은 사용자 값이라 그대로 둔다. codeLabel 은 국문 그대로 — 비교·저장 기준이다.
  //   문서 관리 화면(편집)은 raw 로 원본을 받는다.
  return useMemo(() => {
    if (raw || language !== "en") return base;
    return {
      ...base,
      items: base.items.map((it) => (it.label === it.codeLabel ? { ...it, label: documentLabel(it.label, "en") } : it)),
      families: base.families.map((f) => (f.label === f.codeLabel ? { ...f, label: documentFamilyLabel(f.family, f.label, "en") } : f)),
      modules: base.modules.map((m) => (m.label === m.codeLabel ? { ...m, label: documentLabel(m.label, "en") } : m)),
    };
  }, [base, raw, language]);
}
